import { combineEpics, ofType, StateObservable } from "redux-observable"
import { concat, EMPTY, from, Observable, of } from "rxjs"
import { Action } from "redux"
import {
    catchError,
    map,
    flatMap,
    filter,
    switchMap,
    debounceTime,
    mapTo,
} from "rxjs/operators"

import { Epic } from "../../../../epics"
import { GlobalState } from "../../../../store"
import { datalistAPI } from "../../../../apis"

import {
    addErrorNotification,
    NewNotificationMessage,
} from "../../../notifications/actions"

import { initializeBookmarks } from "../../../bookmarks/actions"
import { initializeShortcuts } from "../../../shortcuts/actions"
import { requestWelcomePageGrid } from "../../../welcomepage/actions"

import * as actions from "./actions"
import { RequestCancelledError } from "../../../../errors"

export const initializeEpic: Epic = (action$, state$, _dependencies$) =>
    action$.pipe(
        ofType(actions.INITIALIZE),
        filter(() => !state$.value.navigation.initialized),
        flatMap(({ tab: selectedTab }: actions.TabAction) =>
            concat(
                of(actions.requestWorkspacesFull()),
                of(actions.requestSmartlists()),
                of(initializeBookmarks()),
                of(initializeShortcuts()),
                of(requestWelcomePageGrid()),
                of(actions.updateSelectedTab(selectedTab)),
                of(actions.setInitialized()),
            ),
        ),
    )

export const requestWorkspacesFullEpic: Epic = (
    action$,
    state$,
    { navigation: { navigationAPI, limit } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_WORKSPACES),
        filter(() => state$.value.navigation.workspaces.hasMore),
        switchMap(() => {
            const {
                navigation: {
                    workspaces: { filter, offset },
                },
            } = state$.value
            return from(
                navigationAPI.getWorkspaceFullHierarchy(
                    filter ?? "",
                    offset,
                    limit + 1,
                ),
            )
        }),
        map((workspaces: Models.NavigationWorkspace[]) => {
            const {
                navigation: {
                    workspaces: { offset },
                },
            } = state$.value
            const hasMore = workspaces.length > limit
            return actions.receiveWorkspacesFull(
                workspaces.slice(0, limit),
                offset + (hasMore ? limit : workspaces.length),
                hasMore,
            )
        }),
    )

export const filterWorkspacesFullEpic: Epic = (
    action$,
    _state$,
    { navigation: { filteringDelay } },
) =>
    action$.pipe(
        ofType(actions.FILTER_WORKSPACES),
        debounceTime(filteringDelay),
        flatMap(() => of(actions.requestWorkspacesFull())),
    )

export const requestSmartlistsEpic: Epic = (action$, _state$) =>
    action$.pipe(
        ofType(actions.REQUEST_SMARTLISTS),
        switchMap(() => {
            return concat(
                of(actions.requestSmartlistsPrivate()),
                of(actions.requestSmartlistsSharedByMe()),
                of(actions.requestSmartlistsSharedWithMe()),
            )
        }),
    )

export const filterSmartlistsEpic: Epic = (
    action$,
    _state$,
    { navigation: { filteringDelay } },
) =>
    action$.pipe(
        ofType(actions.FILTER_SMARTLISTS),
        debounceTime(filteringDelay),
        mapTo(actions.requestSmartlists()),
    )

export const requestSmartlistsPrivateEpic: Epic = (
    action$,
    state$,
    { navigation: { datalistAPI, limit } },
) =>
    requestSmartlistsGeneric(
        action$,
        state$,
        "Private",
        actions.REQUEST_SMARTLISTS_PRIVATE,
        actions.receiveSmartlistsPrivate,
        datalistAPI.getPrivateSmartlists,
        limit,
        "Something went wrong when fetching your private smartlists.",
    )

export const requestSmartlistsSharedByMeEpic: Epic = (
    action$,
    state$,
    { navigation: { datalistAPI, limit } },
) =>
    requestSmartlistsGeneric(
        action$,
        state$,
        "SharedByMe",
        actions.REQUEST_SMARTLISTS_SHARED_BY_ME,
        actions.receiveSmartlistsSharedByMe,
        datalistAPI.getSharedByMeSmartlists,
        limit,
        "Something went wrong when fetching smartlists shared by you.",
    )

export const requestSmartlistsSharedWithMeEpic: Epic = (
    action$,
    state$,
    { navigation: { datalistAPI, limit } },
) =>
    requestSmartlistsGeneric(
        action$,
        state$,
        "SharedWithMe",
        actions.REQUEST_SMARTLISTS_SHARED_WITH_ME,
        actions.receiveSmartlistsSharedWithMe,
        datalistAPI.getSharedWithMeSmartlists,
        limit,
        "Something went wrong when fetching smartlists shared with you.",
    )

const requestSmartlistsGeneric = (
    action$: Observable<Action | number>,
    state$: StateObservable<GlobalState>,
    smartlistType: "Private" | "SharedByMe" | "SharedWithMe",
    requestType:
        | typeof actions.REQUEST_SMARTLISTS_PRIVATE
        | typeof actions.REQUEST_SMARTLISTS_SHARED_BY_ME
        | typeof actions.REQUEST_SMARTLISTS_SHARED_WITH_ME,
    receiveAction:
        | typeof actions.receiveSmartlistsPrivate
        | typeof actions.receiveSmartlistsSharedByMe
        | typeof actions.receiveSmartlistsSharedWithMe,
    fetchFunction:
        | typeof datalistAPI.getPrivateSmartlists
        | typeof datalistAPI.getSharedByMeSmartlists
        | typeof datalistAPI.getSharedWithMeSmartlists,
    limit: number,
    errorMessage: string,
): Observable<actions.ReceiveSmartlistsGeneric | NewNotificationMessage> => {
    return action$.pipe(
        ofType(requestType),
        filter(() => state$.value.navigation.smartlists[smartlistType].hasMore),
        switchMap(() => {
            const {
                navigation: {
                    smartlists: {
                        filter,
                        [smartlistType]: { offset },
                    },
                },
            } = state$.value
            // We fetch an extra smartlist (that won't be shown) so we know whether there is another page
            return from(fetchFunction(filter, offset, limit + 1)).pipe(
                map((smartlists: Models.Smartlist[]) => {
                    const {
                        navigation: {
                            smartlists: {
                                [smartlistType]: { offset },
                            },
                        },
                    } = state$.value
                    return receiveAction(
                        smartlists.slice(0, limit),
                        offset + limit,
                        smartlists.length > limit,
                    )
                }),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return concat(
                        of(receiveAction([], 0, true)),
                        of(addErrorNotification(errorMessage)),
                    )
                }),
            )
        }),
    )
}

export default combineEpics(
    initializeEpic,
    requestWorkspacesFullEpic,
    filterWorkspacesFullEpic,
    requestSmartlistsEpic,
    filterSmartlistsEpic,
    requestSmartlistsPrivateEpic,
    requestSmartlistsSharedByMeEpic,
    requestSmartlistsSharedWithMeEpic,
)
